<template>
  <div>
    <v-toolbar class="toolbar-element" light absolute flat height="90em">
      <v-toolbar-title v-if="!editingEvent" class="toolbar-title">Nuevo evento</v-toolbar-title>
      <v-toolbar-title v-else class="toolbar-title">Reprogramar evento</v-toolbar-title>
    </v-toolbar>
    <div class="spacing-top">
      <v-card>
        <v-alert :value="customErrorPresent" type="error">{{customErrorMessage}}</v-alert>
        <v-card-text>
          <form>
            <v-text-field :disabled="editingEvent" v-model="title" :error-messages="titleErrors" label="Título" maxlength="255"
                          required @input="$v.title.$touch()"></v-text-field>
            <v-textarea :disabled="editingEvent" v-model="message" :error-messages="messageErrors" label="Descripción"
                        required @input="$v.message.$touch()"></v-textarea>
            <v-text-field v-model="location" :error-messages="locationErrors" label="Ubicación" maxlength="100"
                          required @input="$v.location.$touch()"></v-text-field>
            <v-layout row wrap>
              <v-flex xs6 pr-2><DatePickerForm v-model="date" :error-messages="dateErrors"></DatePickerForm></v-flex>
              <v-flex xs6 pl-2><TimePickerForm v-model="time" :error-messages="timeErrors"></TimePickerForm></v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs12>
                <v-autocomplete v-model="departamento" :items="departamentos" itemText="nombre" itemValue="id" label="Departamento"
                                :readonly="saving || editingEvent" :errorMessages="departamentoErrors" @input="$v.departamento.$touch()"
                                :loading="loadingDepartamentos"></v-autocomplete>
              </v-flex>
              <v-flex xs12>
                <SelectItems v-if="!editingEvent" v-model="categorias" :items="categoriasNames" itemText="description" itemValue="id"
                             label="Categorías" :loading="loadingCategorias"></SelectItems>
              </v-flex>
              <v-flex xs6 pr-2>
                <SelectItems v-if="!editingEvent" v-model="personas" :items="personasNames" itemText="nombre" itemValue="id"
                             label="Grupos de invitados" :loading="loadingCriterion"></SelectItems>
              </v-flex>
              <v-flex xs6 pl-2>
                <SelectItems v-if="!editingEvent" v-model="clubes" :items="clubesNames" itemText="nombre" itemValue="pk"
                             label="Clubes" :loading="loadingClubs"></SelectItems>
              </v-flex>
              <v-flex xs12>
                <SelectItems v-if="!editingEvent" v-model="grupos" :items="gruposNames" itemText="nombre" itemValue="id"
                             label="Grupos predeterminados" :loading="loadingGrupos"></SelectItems>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12>
                <EmailCombobox v-if="!editingEvent" ref="emails"></EmailCombobox>
              </v-flex>
            </v-layout>
            <v-flex>
              <AttachFile ref="fileInput" @upload="uploadFiles" description></AttachFile>
            </v-flex>
            <ConfirmationDialog :function-yes="bindingFunction" ref="confirm" title="Abandonar página"
                                text="Se perderán los cambios del formulario actual, ¿desea continuar?"></ConfirmationDialog>
            <v-card-actions>
              <v-btn color="grey" :disabled="saving" @click="goBackConfirmation">Volver</v-btn>
              <v-spacer></v-spacer>
              <v-btn @click="openFileDirectory" color="primary" :disabled="saving" v-if="!editingEvent">
                <span>Agregar adjunto</span>
              </v-btn>
              <v-btn @click="uploadAllFiles" color="primary" :disabled="saving">Enviar</v-btn>
            </v-card-actions>
          </form>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { recipientsMixin } from '../../../mixins/recipientsMixin'
import { createEntityMixin } from '../../../mixins/createEntityMixin'
import { s3UploadMixin } from '../../../mixins/s3UploadMixin'
import DatePickerForm from '../../../common/ui/DatePickerForm'
import TimePickerForm from '../../../common/ui/TimePickerForm'
import SelectItems from '../../../common/ui/SelectItems'
import EmailCombobox from '../../../common/ui/EmailCombobox'
import AttachFile from '../../../common/ui/AttachFile'
import ConfirmationDialog from '../../../common/ui/ConfirmationDialog'

export default {
  mixins: [validationMixin, s3UploadMixin, createEntityMixin, recipientsMixin],

  validations: {
    title: { required },
    location: { required },
    message: { required },
    date: { required },
    time: { required },
    departamento: { required }
  },

  components: {
    DatePickerForm,
    TimePickerForm,
    SelectItems,
    EmailCombobox,
    AttachFile,
    ConfirmationDialog
  },

  data () {
    return {
      location: this.$route.params.ubicacion,
      apiPath: '/event/new',
      apiEditPath: 'event/edit',
      routerPath: 'eventos',
      date: null,
      time: null,
      status: null,
      loadingDepartamentos: false,
      departamentos: [],
      departamento: null
    }
  },

  computed: {
    editingEvent () {
      return this.$route.params.hasOwnProperty('pk')
    },
    locationErrors () {
      const errors = []
      if (!this.$v.location.$dirty) return errors
      !this.$v.location.required && errors.push('La ubicación es requerida.')
      return errors
    },
    departamentoErrors () {
      return this.getRequiredErrors('departamento', 'El departamento es requerido')
    }
  },

  methods: {
    isValidForm () {
      if (this.editingEvent) {
        return !this.$v.$invalid
      } else {
        if (!this.areCustomValidationsValid()) {
          this.handleCustomErrors()
          return false
        } else {
          this.$refs.emails.validateEmails()
          return !this.$v.$invalid && this.$refs.emails.isValid()
        }
      }
    },
    getPayload () {
      let emails = []
      if (!this.editingEvent) {
        emails = this.$refs.emails.getEmails()
      }
      return {
        'status': this.status,
        'title': this.title,
        'description': this.message,
        'ubicacion': this.location,
        'criterios_persona': this.personas,
        'clubes': this.clubes,
        'grupos': this.grupos,
        'horario': `${this.date} ${this.time}`,
        'assets': this.assets,
        'emails': emails,
        'departamento': this.departamento
      }
    },
    fetchEditingEventData () {
      this.status = this.$route.params.status
      this.title = this.$route.params.title
      this.message = this.$route.params.description
      this.location = this.$route.params.ubicacion
      this.personas = this.$route.params.criteriosPersona.map(function (criterio) { return criterio.id })
      this.clubes = this.$route.params.clubes.map(function (club) { return club.pk })
      this.grupos = this.$route.params.grupos.map(function (grupo) { return grupo.id })
      this.date = `${this.$route.params.year}-${this.$route.params.month}-${this.$route.params.day}`
      let hour = this.$route.params.fecha
      this.time = hour.split(' ')[1]
      this.departamento = this.$route.params.departamento
    },
    submit () {
      this.cleanForm()
      const payload = this.getPayload()
      this.saving = true
      if (this.editingEvent) {
        this.axios
          .put(`${this.apiEditPath}/${this.$route.params.pk}`, payload)
          .then(() => {
            this.saving = false
            this.goBack()
          })
          .catch(error => {
            this.$store.commit('setErrorMsg', error)
            this.saving = false
          })
      } else {
        this.axios
          .post(this.apiPath, payload)
          .then(() => {
            this.saving = false
            this.goBack()
          })
          .catch(error => {
            this.saving = false
            this.$store.commit('setErrorMsg', error)
          })
      }
    },
    fetchDepartamentos () {
      this.loadingDepartamentos = true
      this.axios
        .get('departamentos_by_usuario')
        .then(response => {
          this.departamentos = response.data
          if (this.departamentos.length === 1) {
            this.departamento = this.departamentos[0].id
          }
          this.loadingDepartamentos = false
        })
        .catch(error => { this.$store.commit('setErrorMsg', error) })
    }
  },
  created () {
    this.fetchDepartamentos()
  }
}
</script>

<style scoped>
  .spacing-top {
    margin-top: 6em !important;
  }
  .toolbar-element {
    background: linear-gradient(#e2e2e2, #cbd1db);
  }
  .toolbar-title {
    font-size: 2.2em;
    font-family: SourceSansProLight, sans-serif;
    color: #293968;
  }
</style>
